/**
 * Validation for sample receiving, acceptance, pieces, labels and custody.
 *
 * READ CLAUDE.md SECTION 4 FIRST. There is deliberately no schema in this file
 * for a test input, a reading, a dimension, a formula, a rounding rule, an
 * acceptance limit, a chart or an Excel mapping — for any test, including the
 * five initial ones. A sample is validated as a DELIVERY: what arrived, when,
 * from whom, how much of it, and what the laboratory decided about it.
 *
 * FIVE RULES BELOW ARE ACCEPTANCE CRITERIA, NOT PREFERENCES:
 *
 *  1. A QUANTITY TRAVELS WITH ITS UNIT, and neither is optional. A quantity
 *     with no unit is not a fact, and `0` is a real quantity rather than an
 *     absent one (`CLAUDE.md` section 8: an empty field is never zero — and
 *     zero is never empty).
 *
 *  2. HOLDING OR REJECTING REQUIRES A REASON. Accepting requires none.
 *     Withholding work from a client without saying why is not a decision they
 *     can answer or a supervisor can overturn.
 *
 *  3. NO SCHEMA HERE MAKES ANY FIELD MANDATORY BY SAMPLE TYPE (decision
 *     **D70**). The per-type rules are tenant data, enforced by the backend
 *     against the laboratory's own configuration. A type-specific rule written
 *     into this file would be invented science, whatever it looked like.
 *
 *  4. A REPRINT NAMES AN EXISTING IDENTITY AND CANNOT MINT A NEW ONE. There is
 *     no sample number, and no piece index, anywhere in the label schemas.
 *
 *  5. A MOVEMENT IS RECORDED, NEVER EDITED. There is no update schema for a
 *     custody movement, because an editable chain is not a chain (decision
 *     **D75**). A mistake is corrected by recording a correction.
 *
 * The backend re-validates every one of these. The frontend uses them so the
 * user hears about a problem before the round trip.
 */
import { z } from 'zod';
import { ALL_BARCODE_SYMBOLOGIES, ALL_CONFIGURABLE_RECEIVING_FIELDS, ALL_SAMPLE_ACCEPTANCE_DECISIONS, ALL_SAMPLE_MOVEMENT_TYPES, DECISIONS_REQUIRING_REASON, MOVEMENTS_REQUIRING_NOTE, } from '@lims/shared-types';
import { boundedText, isoTimestampSchema, listQuerySchema, reasonSchema, uuidSchema, } from './primitives.js';
// A sample's type is one of its laboratory's own types (0024): a code, checked
// against that laboratory by the backend.
import { additionalFieldsPatchSchema, additionalFieldsSchema, sampleTypeCodeSchema } from './sample-types.js';
const sampleTypeSchema = sampleTypeCodeSchema;
// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------
/** An optional free-text field: absent, or actual content. Never blank. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    // An empty string is an absent value, not a value of "". Storing "" would
    // make an empty field look like a recorded fact.
    .transform((value) => (value === '' ? null : value));
export const sampleAcceptanceDecisionSchema = z.enum(ALL_SAMPLE_ACCEPTANCE_DECISIONS);
export const barcodeSymbologySchema = z.enum(ALL_BARCODE_SYMBOLOGIES);
export const sampleMovementTypeSchema = z.enum(ALL_SAMPLE_MOVEMENT_TYPES);
export const configurableReceivingFieldSchema = z.enum(ALL_CONFIGURABLE_RECEIVING_FIELDS);
/**
 * How much of the material arrived.
 *
 * Non-negative rather than positive, because zero is a real answer a
 * receiving clerk can honestly give — a container that arrived empty is a fact
 * worth recording, and rejecting it would push the clerk into inventing a
 * number. It is still REQUIRED: absent and zero are different things.
 */
const quantitySchema = z
    .number()
    .nonnegative()
    .finite()
    .refine((value) => Number.isFinite(value), {
    message: 'A quantity must be a finite number.',
});
/**
 * What the quantity is counted in, in the laboratory's own words.
 *
 * Free text and not an enum. Prescribing the units would be asserting what
 * form materials arrive in, which differs by laboratory and by material and is
 * not this product's claim to make.
 */
const quantityUnitSchema = boundedText(1, 40);
/**
 * What a sample IS (0023, D289), and its additional details — the fields its
 * own sample type defines in this laboratory (0024, D298), keyed by field id.
 * Not one is a reading, a dimension or a result.
 */
const sampleDetailFields = {
    description: optionalText(500),
    additionalFields: additionalFieldsSchema.optional(),
};
/**
 * The 0023 concrete details, now additional fields of the Concrete type
 * (D300). A request that still sends one is REFUSED by name rather than
 * stripped, so a page loaded before the change cannot lose what was typed.
 */
export const RETIRED_CONCRETE_FIELDS = [
    'concreteSupplier',
    'concreteDesignStrength',
    'concreteElement',
    'concreteCastOn',
];
const retiredFields = Object.fromEntries(RETIRED_CONCRETE_FIELDS.map((field) => [
    field,
    z
        .unknown()
        .optional()
        .refine((value) => value === undefined || value === null || value === '', {
        message: 'Concrete details are now additional fields of the Concrete sample type.',
    }),
]));
// ---------------------------------------------------------------------------
// Receiving
// ---------------------------------------------------------------------------
/**
 * Register a sample that has arrived.
 *
 * The Sample ID is issued by this call, BEFORE any acceptance decision exists
 * (decision **D72**), so there is no status field here: a newly registered
 * sample is `awaiting_inspection` and nothing else. Passing a decision at
 * registration would let one action both receive and judge, and those are
 * separate recorded acts by design.
 *
 * There is no `sampleNumber` field either. The number comes from the tenant's
 * numbering rule and never from the client, or two samples could be handed the
 * same one.
 */
/**
 * The most services one sample may request. Far above any real work order;
 * it only bounds the request.
 */
export const MAX_REQUESTED_SERVICES_PER_SAMPLE = 100;
/**
 * The tests requested for one sample (D215, D216): ids of services on the
 * sample's OWN work order. The API validates every id against that work order
 * in this laboratory and never trusts them; the database checks again.
 */
export const requestedServiceIdsSchema = z
    .array(uuidSchema)
    .max(MAX_REQUESTED_SERVICES_PER_SAMPLE)
    .refine((ids) => new Set(ids).size === ids.length, {
    message: 'Each requested test is listed once.',
});
export const receiveSampleSchema = z.object({
    /**
     * The work order this sample belongs to. Optional, because a sample can
     * physically arrive before the paperwork catches up, and refusing to record
     * it until then would mean the laboratory holds material the system denies
     * having.
     */
    workOrderId: uuidSchema.nullable().optional(),
    /**
     * Add this sample to an existing receipt — another line of the same
     * handover (D193). Absent: the sample opens a NEW receipt with its own
     * number. When given, the work order, the arrival time and the person who
     * delivered must be the receipt's own; the API refuses a mismatch rather
     * than silently overwriting either side.
     */
    receiptId: uuidSchema.nullable().optional(),
    sampleType: sampleTypeSchema,
    /** When it actually arrived, which is not necessarily now. */
    receivedAt: isoTimestampSchema,
    /** Who handed it over. A person or a company, as stated at the counter. */
    receivedFrom: boundedText(2, 200),
    quantity: quantitySchema,
    quantityUnit: quantityUnitSchema,
    // Everything below is optional HERE. A laboratory may make any of them
    // mandatory for a given sample type through its own configuration, and the
    // backend enforces that against the tenant's rules rather than against this
    // schema (decision D70).
    clientReference: optionalText(120),
    sourceDescription: optionalText(500),
    /** When the material was taken, as distinct from when it was received. */
    sampledAt: isoTimestampSchema.nullable().optional(),
    sampledBy: optionalText(200),
    conditionOnReceipt: optionalText(1000),
    storageLocation: optionalText(200),
    notes: optionalText(2000),
    ...sampleDetailFields,
    ...retiredFields,
    /**
     * Which of the work order's services this sample is for (D216). Required
     * (at least one) by the API when the work order has a service that is not
     * cancelled; must be empty without a work order.
     */
    requestedServiceIds: requestedServiceIdsSchema.optional(),
});
/**
 * The most samples one receiving operation takes (D197). A delivery larger
 * than this is received as more than one operation on the same receipt.
 */
export const MAX_SAMPLES_PER_RECEIPT = 50;
/**
 * One SAMPLE of a delivery: only what belongs to that physical sample. The
 * job, the arrival time and the person who delivered are the RECEIPT's and are
 * given once, not per line (D197). Every line becomes its own sample with its
 * own Sample ID; physical samples are never merged into one record.
 */
export const receiptSampleLineSchema = z.object({
    sampleType: sampleTypeSchema,
    quantity: quantitySchema,
    quantityUnit: quantityUnitSchema,
    // Optional here; a laboratory's own per-type rules are enforced by the
    // backend, per line (D70).
    clientReference: optionalText(120),
    sourceDescription: optionalText(500),
    sampledAt: isoTimestampSchema.nullable().optional(),
    sampledBy: optionalText(200),
    conditionOnReceipt: optionalText(1000),
    storageLocation: optionalText(200),
    notes: optionalText(2000),
    ...sampleDetailFields,
    ...retiredFields,
    /**
     * Which of the RECEIPT's work-order services this sample is for (D216).
     * Each line has its own subset; the API requires at least one when the work
     * order has a service that is not cancelled.
     */
    requestedServiceIds: requestedServiceIdsSchema.optional(),
});
const receiptSampleLinesSchema = z
    .array(receiptSampleLineSchema)
    .min(1, 'A receipt records at least one sample.')
    .max(MAX_SAMPLES_PER_RECEIPT, `One receiving operation takes at most ${MAX_SAMPLES_PER_RECEIPT} samples.`);
/**
 * Receive several samples delivered together, in ONE operation (D197).
 *
 * Opens a new numbered receipt and issues a Sample ID to every line, all in one
 * transaction: every sample is registered, or none is and no number is spent.
 * No acceptance decision is taken — each sample is `awaiting_inspection`.
 */
export const receiveSamplesSchema = z.object({
    workOrderId: uuidSchema.nullable().optional(),
    /** When the delivery arrived — shared by every sample on it. */
    receivedAt: isoTimestampSchema,
    /** Who handed it over, as stated at the counter — shared by every sample. */
    receivedFrom: boundedText(2, 200),
    /** Notes about the handover as a whole, kept on the receipt. */
    notes: optionalText(2000),
    samples: receiptSampleLinesSchema,
});
/**
 * Add samples to a receipt already issued (D197): the same delivery, received
 * in more than one step. The job, the arrival time and the deliverer are the
 * receipt's own and are not accepted here, so a line cannot disagree with them.
 */
export const addReceiptSamplesSchema = z.object({
    samples: receiptSampleLinesSchema,
});
/**
 * Edit a sample (D222): its descriptive facts and its requested tests.
 *
 * Every field is optional because this is a patch, but the sample's NUMBER and
 * its arrival time are absent entirely: the first can never change, and the
 * second is what the register and the barcode were built from. The material
 * may be corrected (D224). `workOrderId` is accepted only so that the API can
 * refuse a different one by name (`work_order_not_editable`, D223) instead of
 * silently dropping it. `requestedServiceIds` is the complete new set of
 * requested tests, saved in the same transaction as the fields (D222).
 *
 * D324: a sample that has NO work order may be given one here, once, with
 * `requestedServiceIds` from that work order's services; the link, the tests,
 * any lab work they need and the history are one transaction. A work order a
 * sample already has is still refused (`work_order_not_editable`).
 */
export const updateSampleSchema = z
    .object({
    workOrderId: uuidSchema.nullable().optional(),
    sampleType: sampleTypeSchema.optional(),
    receivedFrom: boundedText(2, 200).optional(),
    quantity: quantitySchema.optional(),
    quantityUnit: quantityUnitSchema.optional(),
    clientReference: optionalText(120),
    sourceDescription: optionalText(500),
    sampledAt: isoTimestampSchema.nullable().optional(),
    sampledBy: optionalText(200),
    conditionOnReceipt: optionalText(1000),
    storageLocation: optionalText(200),
    notes: optionalText(2000),
    description: optionalText(500),
    additionalFields: additionalFieldsPatchSchema.optional(),
    ...retiredFields,
    requestedServiceIds: requestedServiceIdsSchema.optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Provide at least one field to update.',
})
    .refine(
// Changing one half of the pair and not the other would leave a quantity
// described in the old unit.
(value) => (value.quantity === undefined) === (value.quantityUnit === undefined), {
    message: 'A quantity and its unit change together; neither is meaningful alone.',
    path: ['quantityUnit'],
});
// ---------------------------------------------------------------------------
// The acceptance decision
// ---------------------------------------------------------------------------
/**
 * Record what the laboratory decided about a sample.
 *
 * Append-only (decision **D73**): this creates a decision, it never edits one.
 * A held sample that later becomes acceptable gets a second decision, and the
 * first stays visible, because the sequence is the record.
 */
export const decideSampleSchema = z
    .object({
    decision: sampleAcceptanceDecisionSchema,
    /** Required for `held` and `rejected`. Refused as blank for either. */
    reason: reasonSchema.optional(),
    notes: optionalText(2000),
})
    .superRefine((value, ctx) => {
    const needsReason = DECISIONS_REQUIRING_REASON.includes(value.decision);
    if (needsReason && !value.reason) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reason'],
            message: 'Holding or rejecting a sample requires a stated reason, so the ' +
                'client can answer it and a supervisor can review it.',
        });
    }
});
// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------
/**
 * Record one or more pieces of a sample.
 *
 * Pieces are OPTIONAL and never implied (decision **D74**). Nothing here is
 * keyed by sample type, and no count is suggested by one: a sample has the
 * pieces somebody recorded.
 *
 * There is no `pieceCode`. The code is derived from the sample's number and
 * the piece's position, so a piece on a bench names its parent without a
 * lookup and inherits the guarantee that numbers are never reused.
 */
export const addSamplePiecesSchema = z.object({
    /**
     * How many pieces to record. Capped only to stop a typo creating thousands
     * of rows; the cap is not a statement about how many pieces a method needs.
     */
    count: z.number().int().positive().max(500),
    label: optionalText(120),
    notes: optionalText(1000),
});
export const updateSamplePieceSchema = z
    .object({
    label: optionalText(120),
    notes: optionalText(1000),
    storageLocation: optionalText(200),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Provide at least one field to update.',
});
// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------
/**
 * Ask for a label's content, for preview or for printing.
 *
 * There is no sample number and no piece index in this schema. A label names
 * an identity that already exists, which is what makes a reprint incapable of
 * minting a new one — the acceptance criterion "label reprint yields the same
 * identity and creates no new sample number" is satisfied structurally rather
 * than by a check somebody could forget.
 */
export const labelRequestSchema = z.object({
    /** Absent means a label for the sample itself rather than for one piece. */
    pieceId: uuidSchema.optional(),
    /** Overrides the tenant's configured default for this one label. */
    symbology: barcodeSymbologySchema.optional(),
    /** Which language the printed text is in. A label has one at a time. */
    locale: z.enum(['ar', 'en']).optional(),
});
/**
 * Record that a label was physically printed.
 *
 * Separate from asking for its content, because previewing a label is not
 * printing one and the print count must not advance when somebody merely
 * looks. `CLAUDE.md` section 8: a record never advances because a button was
 * pressed — and the converse, that a real event must actually be recorded.
 */
export const recordLabelPrintSchema = z.object({
    pieceId: uuidSchema.optional(),
    symbology: barcodeSymbologySchema.optional(),
    locale: z.enum(['ar', 'en']).optional(),
    /**
     * Why it is being printed again. Optional for a first print, and the reason
     * a laboratory gives is the only way a stack of duplicate labels is ever
     * explainable afterwards.
     */
    reprintReason: optionalText(500),
});
// ---------------------------------------------------------------------------
// Custody and movement
// ---------------------------------------------------------------------------
/**
 * Record a movement. There is deliberately no schema to change one.
 *
 * `occurredAt` is accepted because a movement is often written up after the
 * fact, and forcing "now" would put a false time in a custody chain. It is
 * recorded alongside the insertion time rather than instead of it, so the two
 * can disagree visibly.
 */
export const recordSampleMovementSchema = z
    .object({
    movementType: sampleMovementTypeSchema,
    pieceId: uuidSchema.optional(),
    occurredAt: isoTimestampSchema.optional(),
    fromLocation: optionalText(200),
    toLocation: optionalText(200),
    fromDepartmentId: uuidSchema.nullable().optional(),
    toDepartmentId: uuidSchema.nullable().optional(),
    /** The person who now holds it, for a handover. */
    handedToUserId: uuidSchema.nullable().optional(),
    handedToName: optionalText(200),
    note: optionalText(2000),
})
    .superRefine((value, ctx) => {
    if (MOVEMENTS_REQUIRING_NOTE.includes(value.movementType) &&
        !value.note) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['note'],
            message: 'Returning, disposing of, or correcting the record of a sample ' +
                'requires a note saying what happened.',
        });
    }
});
// ---------------------------------------------------------------------------
// Storage, retention and disposal
// ---------------------------------------------------------------------------
/** Set or clear where a sample is kept. */
export const updateSampleStorageSchema = z.object({
    location: optionalText(200),
    shelfOrBin: optionalText(120),
    note: optionalText(1000),
});
/**
 * Record that a sample was physically disposed of (decision **D71**).
 *
 * This is always a human act. There is no automatic disposal, no scheduled
 * job, and no method enum — a laboratory writes what it did, because
 * prescribing lawful disposal methods for every country this product is sold
 * in is not something the product knows.
 *
 * Disposing of the material does NOT delete the sample. The row, its number
 * and its whole history survive, because the sample existed and a disposed
 * sample is still a sample that existed.
 */
export const disposeSampleSchema = z.object({
    disposedAt: isoTimestampSchema.optional(),
    method: boundedText(3, 500),
    witness: optionalText(200),
    note: optionalText(2000),
});
/**
 * A laboratory's retention rule for one sample type.
 *
 * `null` days means keep until somebody disposes of it, which is the default
 * and destroys nothing. `0` is a real setting meaning due immediately — which
 * still produces a due date and a list entry, never a deletion.
 */
export const setSampleRetentionSchema = z.object({
    sampleType: sampleTypeSchema,
    retentionDays: z.number().int().nonnegative().max(36500).nullable(),
});
/**
 * A laboratory's mandatory-field rule for one sample type (decision **D70**).
 *
 * The mechanism is here; the content is not. An empty `requiredFields` is a
 * legitimate and, until a laboratory says otherwise, the ONLY configuration
 * that exists — no sample type arrives with a field the product decided it
 * needs.
 */
export const setSampleTypeFieldRuleSchema = z.object({
    sampleType: sampleTypeSchema,
    requiredFields: z.array(configurableReceivingFieldSchema).max(ALL_CONFIGURABLE_RECEIVING_FIELDS.length),
});
// ---------------------------------------------------------------------------
// Listing
// ---------------------------------------------------------------------------
/**
 * The sample register.
 *
 * `search` matches the Sample ID, which is what makes a keyboard-wedge scanner
 * work with no integration: it types the barcode's contents into this field
 * exactly as a person would (decision **D68**).
 */
export const sampleListQuerySchema = listQuerySchema.extend({
    status: z.string().trim().max(40).optional(),
    sampleType: sampleTypeSchema.optional(),
    /**
     * One work order's samples, or `none` for the samples linked to no work
     * order (D320). A sample without a work order is not an error.
     */
    workOrderId: z.union([uuidSchema, z.literal('none')]).optional(),
    /**
     * What a page counts (D321). `sample`, the default, pages the samples
     * themselves. `workOrder` pages WORK ORDERS: `page` and `pageSize` count
     * groups, and every group on a page carries its samples, so a work order is
     * never split across two pages. The samples linked to no work order are one
     * group, always the last.
     */
    pageBy: z.enum(['sample', 'workOrder']).default('sample'),
    /**
     * With `pageBy=workOrder`: at most this many samples of each group, newest
     * first. The rest of a larger group is read with `workOrderId` and the
     * ordinary sample paging, so one very large work order can never make a
     * page unbounded.
     */
    groupSampleLimit: z.coerce.number().int().positive().max(100).default(25),
    /** Samples whose disposal due date has passed and that are not yet gone. */
    dueForDisposal: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /** Disposed samples are hidden by default; their rows never leave. */
    includeDisposed: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /**
     * Voided samples are hidden by default; their rows never leave. Asking for
     * them needs `samples.void` (D211, D214).
     */
    includeVoided: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
});
/**
 * The register's work order filter (D322): the laboratory's work orders that
 * have samples, newest first, matched on their number, client or project. A
 * short list per request, searched on the server, so it scales to any number
 * of work orders.
 */
export const sampleWorkOrderOptionsQuerySchema = z.object({
    search: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().positive().max(50).default(20),
    /**
     * `link` (D324): the work orders a sample received without one may be
     * linked to — every one of this laboratory not cancelled, with samples or
     * not. `register` (the default) is the register's filter.
     */
    purpose: z.enum(['register', 'link']).default('register'),
});
/**
 * Correct a sample's requested tests after receiving (D217): the complete new
 * set, at least one. Removing a test already assigned to the sample is refused.
 */
export const updateSampleRequestedServicesSchema = z.object({
    workOrderServiceIds: requestedServiceIdsSchema.refine((ids) => ids.length > 0, {
        message: 'Choose at least one requested test.',
    }),
});
/** Voiding a registered sample (D211). The reason is mandatory. */
export const voidSampleSchema = z.object({
    reason: reasonSchema,
});
/**
 * The Sample Receipts register (D213). `search` matches a receipt number, a
 * sample number on the receipt, a work-order number or a client name.
 */
export const sampleReceiptListQuerySchema = listQuerySchema;
/** The receipt document (D213): its language, and whether to save the file. */
export const sampleReceiptDocumentQuerySchema = z.object({
    language: z.enum(['ar', 'en']),
    download: z
        .enum(['0', '1'])
        .optional()
        .transform((value) => value === '1'),
});
//# sourceMappingURL=samples.js.map